import { useEffect, useCallback, useRef } from "react";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { refreshTokenApi } from "@/features/auth/api/auth.api";

const REFRESH_COOLDOWN_MS = 30_000; // 30 seconds between refresh attempts

export const useAutoRefreshToken = () => {
  const { token, refresh_token, expiresAt } = useAuthStore();
  const refreshingRef = useRef(false);
  const lastRefreshRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasCheckedOnMount = useRef(false);

  // Use refs for values needed inside handleRefresh to keep the callback stable
  const refreshTokenRef = useRef(refresh_token);
  refreshTokenRef.current = refresh_token;

  const storeRef = useRef(useAuthStore.getState);
  storeRef.current = useAuthStore.getState;

  const handleRefresh = useCallback(async () => {
    if (refreshingRef.current) {
      console.log("[AUTO-REFRESH] Skipped — already refreshing");
      return;
    }

    const currentRefreshToken = refreshTokenRef.current;
    const { user, login, logout } = storeRef.current();

    if (!currentRefreshToken || !user) {
      console.log("[AUTO-REFRESH] Skipped — no refresh token or user");
      return;
    }

    // Cooldown: skip if we refreshed recently
    const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
    if (
      lastRefreshRef.current > 0 &&
      timeSinceLastRefresh < REFRESH_COOLDOWN_MS
    ) {
      console.log(
        `[AUTO-REFRESH] Skipped — cooldown (${Math.round(timeSinceLastRefresh / 1000)}s / ${REFRESH_COOLDOWN_MS / 1000}s)`,
      );
      return;
    }

    refreshingRef.current = true;
    console.log("[AUTO-REFRESH] Starting token refresh...");

    try {
      const response = await refreshTokenApi(currentRefreshToken);
      const newToken = response.access_token || response.token;
      const newRefreshToken = response.refresh_token || currentRefreshToken;

      login({
        user: user,
        token: newToken,
        refresh_token: newRefreshToken,
      });

      lastRefreshRef.current = Date.now();
      console.log("[AUTO-REFRESH] ✅ Token refreshed successfully");
    } catch (err) {
      const error = err as { response?: { status?: number } };
      console.log(
        `[AUTO-REFRESH] ❌ Refresh failed (status: ${error?.response?.status})`,
      );

      const isAuthError =
        error?.response?.status === 401 || error?.response?.status === 403;

      if (isAuthError) {
        logout();
        window.location.href = "/login";
      }
    } finally {
      refreshingRef.current = false;
    }
  }, []); // Stable — reads everything from refs

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!token || !refresh_token || !expiresAt) return;

    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;

    if (!hasCheckedOnMount.current) {
      hasCheckedOnMount.current = true;

      if (timeUntilExpiry < 60 * 1000) {
        handleRefresh();
        return;
      }
    }

    const refreshBuffer = 0;
    const timeUntilRefresh = timeUntilExpiry - refreshBuffer;

    if (timeUntilRefresh <= 0) {
      console.log(
        `[AUTO-REFRESH] Token within buffer (${Math.round(timeUntilExpiry / 1000)}s until expiry) — refreshing now`,
      );
      handleRefresh();
    } else {
      console.log(
        `[AUTO-REFRESH] Scheduled refresh in ${Math.round(timeUntilRefresh / 1000)}s (expiry in ${Math.round(timeUntilExpiry / 1000)}s)`,
      );
      timeoutRef.current = setTimeout(() => {
        console.log("[AUTO-REFRESH] Timer fired — refreshing now");
        handleRefresh();
      }, timeUntilRefresh);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }
  }, [token, refresh_token, expiresAt, handleRefresh]);

  // Expose manual refresh for debugging when needed
  return {
    manualRefresh: handleRefresh,
    isRefreshing: refreshingRef.current,
  };
};
